import type { RequestOptions } from "../common/server";
import type { IdOrFilesystemId } from "./types";
import type {
  ExtendedProjectPage,
  InsertLocation,
  PrototypeViewerParams,
  ResponseObject,
  ShortcutCloneJs,
  ShortcutCreateJs,
  ShortcutJs,
  ShortcutLinkAccess,
  ShortcutPagesResponse,
  ShortcutRedirectCommentInfoJs,
  VarsJs,
} from "@/generated/models";

import { supportsUploadProgress } from "@/common/lib/browser";
import { objectToFormData } from "@/services/utils/formData";
import { cloudApiUrl } from "../urls";
import { server } from "./server";

export async function getPrototypeInfo(shortcut: string, { signal }: RequestOptions & { ignoreRedirect?: boolean } = {}) {
  const response = await server.get(`prototype/getPrototypeInfo/${shortcut}`, { signal });
  return await response.json() as ShortcutJs;
}

export async function getShortPrototypeInfo(shortcut: string, accessCode: string | undefined, { signal }: RequestOptions) {
  const response = await server.get(`prototype/getShortPrototypeInfo/${shortcut}`, { signal, searchParams: { code: accessCode } });
  return await response.json() as ShortcutJs;
}

export async function getPrototypePages(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get(`prototype/pages/${shortcut}`, { signal });
  return await response.json() as ShortcutPagesResponse;
}

export function setFeedback(shortcut: string, feedbackEnabled: boolean) {
  const formData = objectToFormData({ feedbackEnabled });
  return server.post(`prototype/setFeedback/${shortcut}`, { body: formData });
}

export async function getViewerParams({ signal }: RequestOptions = {}) {
  const response = await server.get(`prototype/getViewerParams`, { signal });
  return await response.json() as PrototypeViewerParams;
}

export async function getFirstPage(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get(`prototype/getFirstPage/${shortcut}`, { signal });
  return await response.json() as ExtendedProjectPage;
}

export interface ChangeProjectInfo {
  Name: string;
  Password: string;
  fileData: File | null;
  IsUserOnly: boolean | null;
  PublishNote: string | null;
  PublishNoteRichText: string | null;
  Description: string | null;
}
export async function changePrototypeInfo(
  shortcut: string,
  changedProjectInfo: Partial<ChangeProjectInfo>,
  options: RequestOptions = {},
) {
  const formData = objectToFormData(changedProjectInfo);
  const response = await server.post(`prototype/changePrototypeInfo/${shortcut}`, {
    body: formData,
    signal: options.signal,
    onUploadProgress: supportsUploadProgress(cloudApiUrl) ? options.onUploadProgress : undefined,
  });

  return await response.json() as ShortcutJs;
}

export async function uploadPrototypeFile(
  shortcut: string,
  changedProjectInfo: Partial<ChangeProjectInfo>,
  options: RequestOptions = {},
) {
  const formData = objectToFormData(changedProjectInfo);
  const response = await server.post(`prototype/uploadPrototypeFile/${shortcut}`, {
    body: formData,
    signal: options.signal,
    onUploadProgress: supportsUploadProgress(cloudApiUrl) ? options.onUploadProgress : undefined,
    timeout: false, // Disable timeout for file uploads
  });

  return await response.json() as ShortcutJs;
}

export async function getPassword(shortcut: string, { signal }: RequestOptions = {}) {
  const response = await server.get(`prototype/getPassword/${shortcut}`, { signal });
  return await response.json() as VarsJs & { Vars: { password: string } };
}

export type CreateEmptyModel = IdOrFilesystemId & { Name?: string; insertLocation?: InsertLocation; insertAfter?: string };

export async function createEmpty(model: CreateEmptyModel) {
  const formData = objectToFormData(model);
  const response = await server.post("prototype/createEmpty", { body: formData });
  return await response.json() as ShortcutCreateJs;
}

export async function getPrototypeRedirectCommentInfo(shortcut: string) {
  const response = await server.get(`prototype/getPrototypeRedirectCommentInfo/${shortcut}`);
  return await response.json() as ShortcutRedirectCommentInfoJs;
}

export async function cloneShortcut({ shortcut, recaptchaToken }: { shortcut: string; recaptchaToken?: string }) {
  const formData = objectToFormData({ shortcut, recaptchaToken });
  const response = await server.post("prototype/cloneShortcut", { body: formData });
  return await response.json() as ShortcutCloneJs;
}

export async function createPublishNote(shortcut: string, publishNote: string) {
  const formData = objectToFormData({ PublishNoteRichText: publishNote });
  const response = await server.post(`prototype/createPublishNote/${shortcut}`, { body: formData });
  return await response.json() as ResponseObject;
}

export type CanUploadOptions = IdOrFilesystemId & {
  isCreate: boolean;
};

export async function canUpload(options: CanUploadOptions, { signal }: RequestOptions = {}) {
  const response = await server.get("prototype/canUpload", {
    searchParams: options,
    signal,
  });

  return await response.json() as ResponseObject;
}

export async function resetUnreadPublishNotesMentions(shortcut: string) {
  const formData = objectToFormData({ shortcut });
  const response = await server.post("prototype/resetUnreadPublishNotesMentions", { body: formData });
  return await response.json() as ResponseObject;
}

export async function setPrototypeLinkAccess(shortcut: string, linkAccess: ShortcutLinkAccess) {
  const formData = objectToFormData({ shortcut, linkAccess });
  const response = await server.post(`prototype/UpdateLinkAccess`, { body: formData });
  return await response.json() as ResponseObject;
}

export async function reportPrototype(shortcut: string, link: string) {
  const formData = objectToFormData({ shortcut, link });
  await server.post("prototype/reportprototype", { body: formData });
}
